import type { LobbyParty, LobbyCharacterInfo } from '@danda/shared';
import type { LobbyState } from './lobbyStore.types';

export const partiesSlice: Pick<
  LobbyState,
  | 'parties'
  | 'currentPartyId'
  | 'lobbyCharacters'
  | 'setParties'
  | 'addParty'
  | 'updateParty'
  | 'replaceParty'
  | 'removeParty'
  | 'setCurrentParty'
  | 'setLobbyCharacters'
> = {
  parties: [],
  currentPartyId: null,
  lobbyCharacters: [],

  setParties: (parties: LobbyParty[]) => ({ parties }),

  addParty: (party: LobbyParty) => (state: LobbyState) => {
    if (state.parties.some((p) => p.id === party.id)) {
      console.warn(`[addParty] Duplicate party ID ignored: ${party.id}`);
      return {};
    }
    return { parties: [...state.parties, party] };
  },

  updateParty: (id: string, updates: Partial<LobbyParty>) => (state: LobbyState) => ({
    parties: state.parties.map((p: LobbyParty) => (p.id === id ? { ...p, ...updates } : p)),
  }),

  // Full replacement for events that carry complete party snapshots (PartyUpdated,
  // PartyJoined). A shallow merge would preserve stale optional fields the server
  // omits via serde `skip_serializing_if = "Option::is_none"` — e.g. selectedModuleId
  // sticks after session end because the cleared-to-None field is not in the JSON.
  //
  // Exception: two fields are patched optimistically on the client ahead of the
  // server round-trip (`partyImageUrl` via `lobby:background-generated`, and
  // members' `portraitUrl` / `avatarUrl` via `lobby:portrait-generated`). If a
  // `party-updated` races in BEFORE the server has persisted those, a naive
  // replace would flash them off. Preserve the existing values when the incoming
  // snapshot doesn't specify them. Server values always win when present.
  replaceParty: (id: string, party: LobbyParty) => (state: LobbyState) => ({
    parties: state.parties.map((p: LobbyParty) => {
      if (p.id !== id) return p;
      const mergedMembers = party.members?.map((m) => {
        const prev = p.members?.find((e) => e.id === m.id);
        if (!prev) return m;
        return {
          portraitUrl: prev.portraitUrl,
          avatarUrl: prev.avatarUrl,
          ...m,
        };
      });
      return {
        ...party,
        partyImageUrl: party.partyImageUrl ?? p.partyImageUrl,
        members: mergedMembers ?? party.members,
      };
    }),
  }),

  removeParty: (id: string) => (state: LobbyState) => ({
    parties: state.parties.filter((p: LobbyParty) => p.id !== id),
    currentPartyId: state.currentPartyId === id ? null : state.currentPartyId,
  }),

  setCurrentParty: (partyId: string | null) => ({ currentPartyId: partyId }),

  setLobbyCharacters: (characters: LobbyCharacterInfo[]) => ({ lobbyCharacters: characters }),
};
